import { createTheme, alpha } from '@mui/material/styles'

const KEYSYS_DARK = '#41464b'
const KEYSYS_BLUE = '#64b5f6'
// Bottom data-transposition bar accent (panel / tiles / placeholder)
const DATA_TRANSPOSITION_ACCENT = '#00897F'

// Same alpha as palette.divider; reused for derived dashboard tokens
const PALETTE_DIVIDER = alpha('#fff', 0.12)

const buildDashboardColorTokens = () => {
  const dt = DATA_TRANSPOSITION_ACCENT
  return {
    dataTranspositionBar: {
      accent: dt,
      panelSurface: alpha(dt, 0.16),
      panelGradient: `linear-gradient(180deg, ${alpha(dt, 0.22)} 0%, ${alpha(KEYSYS_DARK, 0.08)} 92%)`,
      panelBorder: `1px solid ${alpha(dt, 0.38)}`,
      tileBackground: alpha('#000', 0.28),
      tileBorder: `1px solid ${alpha(dt, 0.42)}`,
      tileInsetShadow: `inset 0 1px 0 ${alpha('#fff', 0.06)}`,
      panelShadowUp: `0 -12px 40px ${alpha('#000', 0.45)}`,
      panelShadowUpOpen: `0 -16px 48px ${alpha('#000', 0.5)}`,
      placeholderBorder: `1px dashed ${alpha(dt, 0.4)}`,
      placeholderBackground: alpha(KEYSYS_DARK, 0.35)
    },
    dashboard: {
      statTile: {
        border: `1px solid ${alpha(PALETTE_DIVIDER, 0.5)}`,
        backgroundColor: alpha(KEYSYS_DARK, 0.2),
        boxShadow: `0 4px 12px ${alpha('#000', 0.15)}, 0 2px 4px ${alpha('#000', 0.1)}`
      },
      chartPaperShadow: `0 4px 20px ${alpha('#000', 0.15)}`,
      loadingStatusPaper: {
        border: `1px solid ${alpha(PALETTE_DIVIDER, 0.7)}`,
        boxShadow: `0 12px 32px ${alpha('#000', 0.35)}`
      }
    }
  }
}

// Drawers, loading chip strip, support/vuln/upgrade accents
const customColors = {
  billingDrawer: {
    accent: '#F57C00',
    background: '#1c1e26',
    surface: '#262a34',
    border: 'rgba(255,255,255,0.08)',
    text: 'rgba(255,255,255,0.92)',
    muted: 'rgba(255,255,255,0.55)'
  },
  dataDrawer: {
    background: '#12141c',
    surface: '#1a1d28',
    border: 'rgba(255,255,255,0.08)',
    text: 'rgba(255,255,255,0.92)',
    muted: 'rgba(255,255,255,0.55)',
    tabIconChart: '#64b5f6',
    tabIconTickets: '#a8d424',
    tabIconVuln: '#f33b59',
    tabIconUpgrades: '#ffd54f'
  },
  supportTypes: {
    support: '#a8d424',
    vulnerability: '#f33b59',
    upgrade: '#ffd54f'
  },
  ticketVisualizer: {
    chipSp: '#4dd0e1'
  },
  ...buildDashboardColorTokens()
}

const theme = createTheme({
  custom: {
    keysys: {
      dark: KEYSYS_DARK,
      blue: KEYSYS_BLUE
    },
    colors: customColors
  },

  palette: {
    mode: 'dark',
    primary: { main: KEYSYS_BLUE },
    background: {
      default: alpha(KEYSYS_DARK, 0.55),
      paper: alpha(KEYSYS_DARK, 0.9)
    },
    divider: PALETTE_DIVIDER,
    text: {
      primary: '#fff',
      secondary: alpha('#fff', 0.75)
    }
  },

  shape: { borderRadius: 14 },

  typography: {
    button: {
      textTransform: 'none',
      fontWeight: 700
    }
  },

  components: {
    MuiPaper: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundImage: 'none',
          // AppBar is a Paper — a full border here reads as a heavy box behind the nav accents
          '&:not(.MuiAppBar-root)': {
            border: `1px solid ${theme.palette.divider}`
          }
        })
      }
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme }) => ({
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: `0 10px 30px ${alpha('#000', 0.35)}`
        })
      }
    },
    MuiAppBar: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: alpha(theme.custom.keysys.dark, 0.88),
          backgroundImage: 'none',
          backdropFilter: 'blur(12px)',
          border: 'none',
          boxShadow: 'none',
          // Single top accent only — no inset shadow (reads like a slab inside the bar)
          borderTop: `1px solid ${alpha('#fff', 0.28)}`,
          overflow: 'visible'
        })
      }
    },
    MuiTabs: {
      styleOverrides: {
        indicator: ({ theme }) => ({
          height: 3,
          borderRadius: 999,
          backgroundColor: theme.palette.primary.main
        })
      }
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          minHeight: 44,
          borderRadius: 12,
          '&.Mui-selected': {
            backgroundColor: alpha(theme.palette.primary.main, 0.18)
          }
        })
      }
    }
  }
})

export { theme }
